import React, { useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { useDeletePromptAnswerMutation } from "../../services/profileAPI";
import AnswerPromptsDialog from "./AnswerPromptsDialog";
import { useRouter } from "next/router";

type PromptCardPropsType = {
  questionId: string;
  question: string;
  answer: string;
  isSameUser: boolean;
};

function PromptCard({
  questionId,
  question,
  answer,
  isSameUser,
}: PromptCardPropsType) {
  const router = useRouter();
  const [openAnswerPrompt, setOpenAnswerPrompt] = useState(false);

  const [deletePromptAnswer, { isLoading: isDeletePromptLoading }] =
    useDeletePromptAnswerMutation();
  const { enqueueSnackbar } = useSnackbar();

  const confirm = useConfirm();

  const closeAnswerPromptDialog = () => {
    setOpenAnswerPrompt(false);
  };

  const openAnswerPromptDialog = () => {
    setOpenAnswerPrompt(true);
  };

  const openDeleteConfirmation = () => {
    confirm({
      description:
        "are you sure you want to remove this prompt? the currently added text will be deleted",
      title: "remove prompt",
      confirmationText: "delete",
      cancellationText: "cancel",
      confirmationButtonProps: {
        disabled: isDeletePromptLoading ? true : false,
      },
    })
      .then(() => {
        deletePrompt(questionId);
      })
      .catch(() => {});
  };

  const deletePrompt = async (questionId: string) => {
    try {
      const response = await deletePromptAnswer({
        questionId: questionId!,
      }).unwrap();
      if (response) {
        enqueueSnackbar("prompt deleted successfully", {
          variant: "success",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        "an error occured while deleting the prompt. please try again later";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  return (
    <>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        key={questionId}
        sx={{
          backgroundColor: "#F2CCC3",
          height: "250px",
          position: "relative",
        }}
      >
        {isSameUser && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 3,
            }}
          >
            <IconButton onClick={() => openAnswerPromptDialog()}>
              <EditOutlinedIcon />
            </IconButton>
            <IconButton onClick={() => openDeleteConfirmation()}>
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        )}
        <Typography
          variant="subtitle1"
          color="text.secondary"
          fontWeight="400"
          gutterBottom
          sx={{ textAlign: { xs: "center", md: "" } }}
        >
          {question}
        </Typography>
        <Typography
          fontWeight="600"
          fontSize={18}
          sx={{ textAlign: { xs: "center", md: "" }, padding: "0px 44px" }}
        >
          {answer}
        </Typography>
      </Stack>
      {openAnswerPrompt && (
        <AnswerPromptsDialog
          open={openAnswerPrompt}
          onClose={closeAnswerPromptDialog}
          question={question}
          answer={answer}
          questionId={questionId}
        />
      )}
    </>
  );
}

export default PromptCard;
